import type { Produto } from '@/api/endpoints/orcamentos';

export interface KitItem {
  produto: Produto;
  quantidade: number;
}

export interface OrcamentoParams {
  cambio: number; // USD -> BRL
  iiPct: number; // imposto de importação, ex 60
  icmsPct: number; // ex 17
  freteIntlUsd: number; // frete internacional (rateio simplificado)
  margemPct: number; // margem sobre custo do hardware, ex 40
  freteNacional: number; // R$ (manual ou Melhor Envio)
  descontoBrincoPct: number; // desconto nos brincos em %
  descontoBrincoReais: number; // desconto por brinco em R$/unidade
}

export interface OrcamentoResultado {
  // hardware = leitor / aplicador / acessório (custo + imposto + margem)
  fobImportadosBrl: number;
  valorII: number;
  valorICMS: number;
  landedImportados: number;
  itensNacionais: number;
  custoHardware: number;
  valorMargem: number;
  hardwareVenda: number; // custo do hardware + margem (sem frete)
  // brincos = preço de venda fixo (sem margem/imposto), com desconto
  brincoQtd: number;
  brincoBruto: number;
  brincoDesconto: number;
  brincoLiquido: number;
  // fechamento
  freteNacional: number;
  totalGeral: number; // hardware + brincos + frete
  pesoTotalKg: number;
}

const isBrinco = (p: Produto) => p.categoria === 'brinco';
const isImportado = (p: Produto) => p.moedaCusto === 'USD';

// Preço/custo unitário em R$ (converte USD pelo câmbio).
export function valorUnitarioBrl(p: Produto, cambio: number): number {
  return isImportado(p) ? p.custoUsd * cambio : p.custoBrl;
}

// Pipeline de precificação:
// - HARDWARE (leitor/aplicador/acessório): custo → II + ICMS (só importados) → margem.
// - BRINCOS: preço de venda fixo (R$ por unidade) × qtd, MENOS desconto (% e/ou R$/un).
//   Não sofrem imposto de importação nem margem — o preço já é o final ao cliente.
export function calcularOrcamento(itens: KitItem[], p: OrcamentoParams): OrcamentoResultado {
  const cambio = p.cambio || 0;
  const ii = (p.iiPct || 0) / 100;
  const icms = (p.icmsPct || 0) / 100;
  const margem = (p.margemPct || 0) / 100;

  let fobImpUsd = 0; // hardware importado (USD)
  let nacionalBrl = 0; // hardware nacional (BRL)
  let brincoQtd = 0;
  let brincoBruto = 0;
  let pesoG = 0;

  for (const { produto, quantidade } of itens) {
    const qty = quantidade || 0;
    pesoG += (produto.pesoG || 0) * qty;

    if (isBrinco(produto)) {
      brincoQtd += qty;
      brincoBruto += valorUnitarioBrl(produto, cambio) * qty;
    } else if (isImportado(produto)) {
      fobImpUsd += (produto.custoUsd || 0) * qty;
    } else {
      nacionalBrl += (produto.custoBrl || 0) * qty;
    }
  }

  // Hardware importado: landed cost (II + ICMS por dentro)
  const valorAduaneiroUsd = fobImpUsd + (p.freteIntlUsd || 0);
  const fobImpBrl = fobImpUsd * cambio;
  const aduaneiroBrl = valorAduaneiroUsd * cambio;
  const valorII = aduaneiroBrl * ii;
  const baseAntesIcms = aduaneiroBrl + valorII;
  const baseIcms = icms < 1 ? baseAntesIcms / (1 - icms) : baseAntesIcms;
  const valorICMS = baseIcms - baseAntesIcms;
  const landedImportados = baseAntesIcms + valorICMS;

  const custoHardware = landedImportados + nacionalBrl;
  const valorMargem = custoHardware * margem;
  const hardwareVenda = custoHardware + valorMargem;

  // Brincos: preço fixo menos desconto (% + R$/unidade), sem passar de 100%
  const descPct = brincoBruto * ((p.descontoBrincoPct || 0) / 100);
  const descReais = (p.descontoBrincoReais || 0) * brincoQtd;
  const brincoDesconto = Math.min(brincoBruto, descPct + descReais);
  const brincoLiquido = Math.max(0, brincoBruto - brincoDesconto);

  const freteNacional = p.freteNacional || 0;
  const totalGeral = hardwareVenda + brincoLiquido + freteNacional;

  return {
    fobImportadosBrl: fobImpBrl,
    valorII,
    valorICMS,
    landedImportados,
    itensNacionais: nacionalBrl,
    custoHardware,
    valorMargem,
    hardwareVenda,
    brincoQtd,
    brincoBruto,
    brincoDesconto,
    brincoLiquido,
    freteNacional,
    totalGeral,
    pesoTotalKg: pesoG / 1000,
  };
}

export const brl = (v: number) =>
  'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
